/**
 * The right drawer of the new layout: config, a profile, details or a second conversation, opened by any page
 * (ShellContext useRightDrawer / RightDrawerPortal). It slides over the right of the workspace (wide screens keep
 * the page visible beside it), Escape or the X closes it; on a phone it is a full screen sheet.
 */
import { useEffect } from 'react';
import { X } from 'lucide-react';
import { cn } from '@/components/ui/utils';
import { useShell } from './ShellContext';

export function RightDrawer() {
  const shell = useShell();
  const d = shell?.drawer || null;
  const close = shell?.closeDrawer;
  useEffect(() => {
    if (!d || !close) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [d, close]);
  if (!shell) return null;
  return (
    <aside
      aria-label={d?.title || 'Details'}
      aria-hidden={!d}
      data-right-drawer={d?.id || ''}
      style={shell.isPhone ? undefined : { width: d?.width || 400 }}
      className={cn('flex min-h-0 flex-col overflow-hidden bg-[var(--shell-surface)] text-[var(--shell-text)] transition-[transform,opacity] duration-200',
        shell.isPhone ? 'fixed inset-0 z-[80]' : 'absolute bottom-2 right-2 top-0 z-30 rounded-[16px] border border-[var(--shell-border)] shadow-[0_16px_48px_rgba(16,24,40,0.18)]',
        d ? 'visible translate-x-0 opacity-100' : 'pointer-events-none invisible translate-x-6 opacity-0')}
    >
      <header className="flex items-center gap-2 px-4 pb-2 pt-3.5">
        <h2 className="min-w-0 flex-1 truncate text-[15px] font-semibold" style={{ fontFamily: 'var(--font-display)' }}>{d?.title}</h2>
        <button type="button" onClick={() => shell.closeDrawer()} aria-label="Close" title="Close (Esc)" className="grid h-8 w-8 place-items-center rounded-full text-[var(--shell-muted)] hover:bg-[var(--shell-hover)] hover:text-[var(--shell-text)]"><X className="h-4 w-4" /></button>
      </header>
      {/* Pages put their own (light) controls here, so the body keeps the light tokens in dark mode too. */}
      <div className={cn('min-h-0 flex-1 overflow-y-auto pb-4', d && d.content === undefined && 'bg-[var(--shell-surface)] text-[#101828]')} data-light-surface={d && d.content === undefined ? '' : undefined}>
        {d?.content}
        {/* Live content from RightDrawerPortal lands here. */}
        <div ref={shell.setDrawerHost} className="contents" />
      </div>
    </aside>
  );
}
